import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Sparkles } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const location = useLocation();
  const nav = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (!res.ok) {
      setError(res.error);
      toast.error(res.error);
      return;
    }
    toast.success("Welcome back!");
    const redirect = location.state?.from || (res.user.role === "consumer" ? (res.user.onboarded ? "/app" : "/onboarding") : "/admin");
    // Hard navigation to ensure AuthContext picks up the new token cleanly
    window.location.href = redirect;
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] grid lg:grid-cols-2">
      <div className="hidden lg:flex bg-emerald-700 text-white p-12 items-center">
        <div>
          <Sparkles className="w-10 h-10 mb-6" />
          <h2 className="font-display font-bold text-4xl tracking-tight leading-tight">
            Welcome back. Your cashback is waiting.
          </h2>
          <p className="text-emerald-50 mt-4 text-lg leading-relaxed">
            Track your savings, see what Ria recommends today, and cash out anytime.
          </p>
          <ul className="mt-8 space-y-3 text-emerald-50">
            <li>· Pending, approved & lifetime cashback</li>
            <li>· AI Smart Savings Feed</li>
            <li>· Family Vault & Savings Goals</li>
          </ul>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 lg:p-12">
        <Card className="w-full max-w-md border-gray-200 shadow-none">
          <CardContent className="p-8">
            <h1 className="font-display font-bold text-3xl tracking-tight text-gray-900">Log in</h1>
            <p className="text-gray-600 mt-1.5">Welcome back to LoyaltyCircle AI.</p>

            <form onSubmit={handleSubmit} className="mt-8 space-y-4" data-testid="login-form">
              <div>
                <Label htmlFor="email" className="mb-1.5 block">Email</Label>
                <Input
                  id="email"
                  type="email"
                  required
                  data-testid="login-email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                />
              </div>
              <div>
                <Label htmlFor="password" className="mb-1.5 block">Password</Label>
                <Input
                  id="password"
                  type="password"
                  required
                  data-testid="login-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                />
              </div>
              {error && (
                <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-3" data-testid="login-error">
                  {error}
                </div>
              )}
              <Button
                type="submit"
                disabled={loading}
                data-testid="login-submit"
                className="w-full bg-emerald-700 hover:bg-emerald-800 text-white rounded-full py-2.5 font-semibold"
              >
                {loading ? "Logging in…" : "Log in"}
              </Button>
            </form>

            <p className="text-sm text-gray-600 mt-6 text-center">
              New here?{" "}
              <Link to="/signup" className="text-emerald-700 font-semibold hover:underline" data-testid="login-to-signup">
                Create an account
              </Link>
            </p>

            <div className="mt-8 border-t border-gray-200 pt-6">
              <p className="text-xs text-gray-500 mb-2">Try the demo:</p>
              <button
                type="button"
                onClick={() => { setEmail("demo@loyaltycircle.ai"); setPassword("Demo@123456"); }}
                className="text-xs text-sky-700 hover:underline"
                data-testid="fill-demo-creds"
              >
                Fill demo consumer credentials
              </button>
              <span className="mx-2 text-gray-300">·</span>
              <button
                type="button"
                onClick={() => { setEmail("admin@loyaltycircle.ai"); setPassword("Admin@123456"); }}
                className="text-xs text-sky-700 hover:underline"
                data-testid="fill-admin-creds"
              >
                Fill admin credentials
              </button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
